/**
 * Single source of truth for the authenticated session token.
 *
 * The backend issues one JWT (see `app/lib/json_web_token.rb`) carrying two
 * horizons: a 1-hour access window and a 3-month refresh window. We persist it
 * in `localStorage` so the session is shared across every tab/window of the same
 * origin and survives a reload (and a browser restart) until logout / a terminal
 * 401. Cross-tab changes are observable via `onAuthTokenChange`.
 *
 * Responsibility is limited to persistence: read/write/clear/subscribe. The
 * transport layer (`http.ts`) reads the token to authenticate requests and
 * refreshes it; the auth composable owns the reactive "is connected" state.
 * No Vue reactivity and no API calls live here.
 *
 * Every accessor is SSR-safe: during Vite-SSG prerendering there is no
 * `window`, so reads return `null`, writes are no-ops, and subscriptions detach.
 */

const STORAGE_KEY = 'smerra:auth:token'

/** True only in a browser context (guards SSR/prerender). */
function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

/** The current auth token, or `null` when absent / outside the browser. */
export function getAuthToken(): string | null {
  if (!isBrowser()) return null
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    // localStorage can throw (privacy mode, disabled storage): treat as absent.
    return null
  }
}

/** Persist the auth token, shared across all tabs/windows. No-op outside the browser. */
export function setAuthToken(token: string): void {
  if (!isBrowser()) return
  try {
    window.localStorage.setItem(STORAGE_KEY, token)
  } catch {
    /* quota or storage disabled: the caller keeps any in-memory copy it holds */
  }
}

/** Remove the persisted auth token (logout / terminal 401). No-op outside the browser. */
export function clearAuthToken(): void {
  if (!isBrowser()) return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* no-op */
  }
}

/** Whether a token is currently persisted. */
export function hasAuthToken(): boolean {
  return getAuthToken() !== null
}

/**
 * Subscribe to token changes made in *other* tabs/windows (the `storage` event
 * never fires in the tab that made the change). The handler receives the new
 * token value (or `null` when it was cleared) so the caller can keep its
 * reactive auth state in sync across tabs.
 *
 * Returns an unsubscribe function. No-op (returns a noop) outside the browser.
 */
export function onAuthTokenChange(handler: (token: string | null) => void): () => void {
  if (!isBrowser()) return () => {}
  const listener = (event: StorageEvent) => {
    if (event.storageArea !== window.localStorage) return
    // `key === null` means localStorage was cleared wholesale — re-read either way.
    if (event.key !== null && event.key !== STORAGE_KEY) return
    handler(getAuthToken())
  }
  window.addEventListener('storage', listener)
  return () => window.removeEventListener('storage', listener)
}
